import React, { useState, KeyboardEvent } from 'react';
import {
  Popover,
  TextField,
  Button,
  Stack,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import { magicButtonProps } from '../../common/ai';
import ContextReferenceSelector from '../../common/ContextReferenceSelector';

interface GenerateContentDialogProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSubmit: (instruction: string, fileIds?: string[]) => void;
  lang: string;
  title?: string;
  loading?: boolean;
  elementType?: "text" | "picture" | "table" | "chart" | "shape";
  preDefinedInstructions?: Array<{ en: string; zh: string }>;
  placeholder?: string;
  projectId: string;
}

function GenerateContentDialog({ 
  open, 
  anchorEl,
  onClose, 
  onSubmit, 
  lang,
  title,
  loading = false,
  elementType,
  preDefinedInstructions = [],
  placeholder,
  projectId,
}: GenerateContentDialogProps) {
  const [instruction, setInstruction] = useState('');
  const [fileIds, setFileIds] = useState<string[] | undefined>();

  const handleClose = () => {
    setInstruction('');
    setFileIds(undefined);
    onClose();
  };

  const handleSubmit = () => {
    const trimmedInstruction = instruction.trim();
    handleClose();
    onSubmit(trimmedInstruction, fileIds);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      if (event.shiftKey) {
        // Allow new line with Shift+Enter
        return;
      }
      // Submit with just Enter
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleInstructionChipClick = (instructionText: string) => {
    setInstruction(instructionText);
  };

  // Only show predefined instructions when generating for text elements or when no specific element type is provided
  const shouldShowPredefinedInstructions = elementType === 'text' || elementType === 'shape';

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: { 
            width: 320,
            '& .MuiTypography-root': {
              fontSize: '0.875rem',
            },
            '& .MuiButton-root': {
              fontSize: '0.75rem',
              py: 0.5,
            },
          }
        }
      }}
    >
      <Box sx={{ p: 1.5 }}>
        <Stack spacing={1.5}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {title || (lang === 'en' ? 'Generate Content' : '生成内容')}
          </Typography>
          
          <Box>
            <ContextReferenceSelector
              lang={lang}
              projectId={projectId}
              size="small"
              onFileIdsChange={setFileIds}
            />
          </Box>

          {shouldShowPredefinedInstructions && (
            <Box>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {preDefinedInstructions.map((option) => (
                  <Chip
                    key={option.en}
                    label={lang === 'en' ? option.en : option.zh}
                    size="small"
                    onClick={() => handleInstructionChipClick(lang === 'en' ? option.en : option.zh)}
                    sx={{ mb: 0.5, fontSize: '0.75rem' }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          <TextField
            variant="filled"
            autoFocus
            size="small"
            label={lang === 'en' ? 'Additional Instructions (Optional)' : '附加指令（可选）'}
            fullWidth
            multiline
            rows={2}
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || (lang === 'en' 
              ? 'Enter any specific instructions for content generation...' 
              : '输入任何具体的内容生成指令...')}
            helperText={lang === 'en' 
              ? 'Press Enter to submit, Shift+Enter for new line' 
              : '按回车键提交，Shift+回车键换行'}
            InputProps={{
              sx: { fontSize: '0.875rem' }
            }}
            InputLabelProps={{
              sx: { fontSize: '0.875rem' }
            }}
            FormHelperTextProps={{
              sx: { fontSize: '0.75rem', mt: 0.5 }
            }}
          />

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={handleClose} size="small">
              {lang === 'en' ? 'Cancel' : '取消'}
            </Button>
            <Button
              onClick={handleSubmit}
              size="small"
              {...magicButtonProps({ loading })}
            >
              {lang === 'en' ? 'Generate' : '生成'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Popover>
  );
}

export default GenerateContentDialog; 